import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import BACKOFFICE_BASE_API_V1 from "../../utils/backofficeApi";
import { createReactCheckout } from "../../utils/paymentsApi";
import "./PricingPage.css";

const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "number") {
    return `${value} ден`;
  }

  const raw = String(value).trim();
  if (!raw) {
    return "—";
  }

  return raw.includes("ден") ? raw : `${raw} ден`;
};

const getProductStatusMeta = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "published") {
    return { label: "Достапно", className: "pricing-status-chip published" };
  }

  if (normalized === "unpublished") {
    return { label: "Недостапно", className: "pricing-status-chip unpublished" };
  }

  if (normalized === "active") {
    return { label: "Достапно", className: "pricing-status-chip published" };
  }

  return { label: status || "Непознато", className: "pricing-status-chip" };
};

const sanitizeDescriptionHtml = (value) => {
  if (!value) {
    return "";
  }

  if (typeof window === "undefined") {
    return String(value);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(String(value), "text/html");

  doc.querySelectorAll("script, style, iframe, object, embed").forEach((node) => {
    node.remove();
  });

  const allowedTags = new Set(["P", "BR", "STRONG", "EM", "B", "I", "UL", "OL", "LI"]);
  Array.from(doc.body.querySelectorAll("*")).forEach((node) => {
    if (!allowedTags.has(node.tagName)) {
      node.replaceWith(doc.createTextNode(node.textContent || ""));
      return;
    }
    Array.from(node.attributes).forEach((attr) => node.removeAttribute(attr.name));
  });

  return doc.body.innerHTML;
};

const PricingPage = () => {
  const { authPayload } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutLoadingKey, setCheckoutLoadingKey] = useState("");

  useEffect(() => {
    let active = true;

    const loadProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await fetch(
          `${BACKOFFICE_BASE_API_V1}/products/react_products`,
          {
          method: "GET",
          }
        );

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || "Неуспешно вчитување на ценовник.");
        }

        const data = await response.json();
        if (!active) {
          return;
        }

        const nextProducts = Array.isArray(data?.products) ? data.products : [];
        setProducts(nextProducts);
      } catch (err) {
        if (!active) {
          return;
        }
        console.error("Failed to fetch react products", err);
        setError(err.message || "Настана грешка при вчитување на ценовникот.");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadProducts();
    return () => {
      active = false;
    };
  }, []);

  const handleStartCheckout = async (product) => {
    if (!authPayload?.id_token || !authPayload?.user_email) {
      setCheckoutError("За купување најавете се со валиден профил.");
      return;
    }

    setCheckoutError("");
    const loadingKey = `${product.id}`;
    setCheckoutLoadingKey(loadingKey);

    try {
      const result = await createReactCheckout({
        productId: Number(product.id) || product.id,
        variantId: 151515,
        email: authPayload.user_email,
        redirectUrl: `${window.location.origin}/checkout/success`,
      });

      if (!result?.checkout_url || !result?.purchase_reference) {
        throw new Error("Недостасуваат checkout податоци од серверот.");
      }

      sessionStorage.setItem(
        "lastCheckoutPurchaseReference",
        String(result.purchase_reference)
      );
      localStorage.setItem(
        "lastCheckoutPurchaseReference",
        String(result.purchase_reference)
      );

      window.location.assign(result.checkout_url);
    } catch (err) {
      console.error("Checkout creation failed", err);
      setCheckoutError(err.message || "Неуспешно креирање на checkout.");
    } finally {
      setCheckoutLoadingKey("");
    }
  };

  return (
    <div className="pricing-page">
      <section className="pricing-hero">
        <div className="pricing-hero-content">
          <Typography variant="overline" className="pricing-eyebrow">
            KAZHAVDA PRICING
          </Typography>
          <Typography variant="h3" component="h1" className="pricing-title">
            Избери што ти треба за свадбата
          </Typography>
          <Typography variant="body1" className="pricing-subtitle">
            Две опции: пристап до дигитална покана и надградба за
            неограничен број гости.
          </Typography>
        </div>
      </section>

      <section className="pricing-grid">
        {!authPayload?.id_token ? (
          <div className="pricing-state-card">
            <Alert severity="info" sx={{ width: "100%" }}>
              За купување најавете се. <Link to="/login">Најава</Link>
            </Alert>
          </div>
        ) : null}

        {checkoutError ? (
          <div className="pricing-state-card">
            <Alert severity="error" sx={{ width: "100%" }}>
              {checkoutError}
            </Alert>
          </div>
        ) : null}

        {loading ? (
          <div className="pricing-state-card">
            <CircularProgress size={26} />
            <Typography variant="body2">Вчитување производи...</Typography>
          </div>
        ) : null}

        {!loading && error ? (
          <div className="pricing-state-card">
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
          </div>
        ) : null}

        {!loading && !error && products.length === 0 ? (
          <div className="pricing-state-card">
            <Typography variant="body2" color="text.secondary">
              Моментално нема достапни производи.
            </Typography>
          </div>
        ) : null}

        {!loading && !error
          ? products.map((product) => (
          (() => {
            const statusMeta = getProductStatusMeta(product.status);
            return (
          <article key={product.id} className="pricing-card">
            <div className="pricing-image-wrap">
              <img
                src={
                  product.photo ||
                  "https://placehold.co/1000x1000/f6f4ef/6b615b?text=Kazhavda"
                }
                alt={product.name || "Product"}
                className="pricing-image"
                loading="lazy"
              />
            </div>
            <div className="pricing-card-top">
              <Typography variant="h5" className="pricing-card-title">
                {product.name}
              </Typography>
              <Chip
                label={statusMeta.label}
                size="small"
                className={statusMeta.className}
              />
            </div>

            <div
              className="pricing-card-description"
              dangerouslySetInnerHTML={{
                __html: sanitizeDescriptionHtml(product.description),
              }}
            />

            <Stack spacing={1.25} className="pricing-variants">
              {(Array.isArray(product.variants) ? product.variants : []).map((variant) => (
                <Box key={variant.id} className="pricing-variant-row">
                  <div>
                    <Typography variant="subtitle2">{variant.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Variant ID: {variant.id}
                    </Typography>
                  </div>
                  <div className="pricing-variant-meta">
                    <Typography variant="subtitle1" className="pricing-price">
                      {formatPrice(variant.price)}
                    </Typography>
                    {variant.test_mode ? (
                      <Chip label="Test mode" size="small" />
                    ) : null}
                  </div>
                </Box>
              ))}
            </Stack>

            <Button
              variant="contained"
              className="pricing-button"
              disabled={
                checkoutLoadingKey === String(product.id) ||
                !authPayload?.id_token ||
                String(product.status || "").toLowerCase() !== "published"
              }
              onClick={() => handleStartCheckout(product)}
            >
              {checkoutLoadingKey === String(product.id) ? "Се креира..." : "Купи"}
            </Button>
          </article>
            );
          })()
            ))
          : null}
      </section>
    </div>
  );
};

export default PricingPage;
